const MAX_NEXT_PATH_LENGTH = 512;

// biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control chars is the point
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/** Returns `value` only if it is a same-origin relative path (single leading "/",
 * no "//", no backslash, no control chars, length-capped); otherwise null.
 * Mirrors app/utils/redirects.py::safe_next_path. */
export const safeNextPath = (value: string | null | undefined): string | null => {
  if (!value || value.length > MAX_NEXT_PATH_LENGTH) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  if (value.includes('\\') || CONTROL_CHARS.test(value)) return null;
  return value;
};

/** Reads and validates the `next` query param from a search string. */
export const readNextParam = (search: string): string | null => safeNextPath(new URLSearchParams(search).get('next'));

/** Route paths of the first-run gates (OnboardingGuard, PiiTrainingGuard redirect targets). */
const FIRST_RUN_GATE_PATHS = ['/onboarding', '/pii-training'];

/** True when `path`'s pathname is a first-run gate page; a gate must never be its own return target. */
export const isFirstRunGatePath = (path: string): boolean => {
  const pathname = new URL(path, window.location.origin).pathname.replace(/\/+$/, '');
  return FIRST_RUN_GATE_PATHS.includes(pathname);
};

/** `next` for leaving a first-run gate: like readNextParam, but a gate path is rejected (null). */
export const readGateExitNextParam = (search: string): string | null => {
  const next = readNextParam(search);
  return next && !isFirstRunGatePath(next) ? next : null;
};

/** `base` URL carrying `path` (the current location) as the return target. */
export const urlWithNext = (base: string, path: string): string => {
  const next = safeNextPath(path);
  return next && next !== '/' ? `${base}?next=${encodeURIComponent(next)}` : base;
};

/** `/login` URL carrying the current location as the return target. */
export const loginUrlWithNext = (path: string): string => urlWithNext('/login', path);
